// Die Bausteine der Handy-Apps (seit E2) – nach dem Mockup
// (mockup/js/ansichten-doku.js, appKopf, platzhalter, Chips, Toast). Das
// Aussehen steht in marke.css; hier nur, was sich wiederholt.

import { useEffect, useSyncExternalStore, type ReactNode } from "react";
import type { Umgebung } from "@happy-energy/kern";
import { Ikon, type IkonName } from "./Ikon.tsx";

/** Das orangefarbene Band in Test-Umgebung und am Laptop – wie im Kopf des CRM. */
export function TestBand({ umgebung }: { umgebung: Umgebung }) {
  if (umgebung === "echt") return null;
  return <div className="test-band">{umgebung === "test" ? "TEST-UMGEBUNG – nur Spieldaten" : "AM LAPTOP – nur Spieldaten"}</div>;
}

/** Wohin der Pfeil oben links führt – mit dem Namen des Ziels, z. B. „‹ Übersicht“. */
export interface KopfZurueck {
  adresse: string;
  /** Kurz, ein bis zwei Wörter: „Start“, „Übersicht“, „Station 3“, „Foto“. */
  name: string;
  /**
   * Ein kleines Zeichen vor dem Namen – „haus“ überall, wo die Startseite
   * das Ziel ist: „‹ ⌂ Start“ (Gernot, Augentest 19.10.2026). So sieht man
   * auf einen Blick, welcher Pfeil ganz nach Hause führt.
   */
  ikon?: "haus";
}

/**
 * Der schwarze Kopf einer Seite: links der Pfeil mit dem Namen des Ziels
 * (oder das Zeichen der App), in der Mitte ein kleiner Titel, rechts Platz
 * für einen Chip; darunter groß, worum es geht, und eine Zeile dazu.
 *
 * Die obere Zeile ist ein Raster mit drei Spalten: links und rechts gleich
 * breit, der Titel genau in der Mitte. Wird es eng, kürzt sich zuerst der
 * Titel (er steht ohnehin groß darunter) – der Weg zurück bleibt lesbar.
 * Seit 19.10.2026 (Augentest am iPhone: Pfeil, Titel und Chip saßen schief,
 * und ein Pfeil ohne Wort sagte nicht, wohin er führt).
 */
export function AppKopf({
  umgebung,
  zurueck,
  titel,
  gross,
  unter,
  rechts,
  aendern,
  children,
}: {
  umgebung: Umgebung;
  /** Wohin „Zurück“ führt und wie das Ziel heißt – ohne: das Zeichen der App. */
  zurueck?: KopfZurueck;
  titel?: ReactNode;
  gross?: ReactNode;
  unter?: ReactNode;
  rechts?: ReactNode;
  /** Wohin „ändern“ führt: Dann sind die große Zeile und die darunter ein Knopf, mit kleinem Stift „ändern“. */
  aendern?: string;
  children?: ReactNode;
}) {
  return (
    <>
      <TestBand umgebung={umgebung} />
      <header className="app-kopf">
        <div className="app-kopf-oben">
          <div className="app-kopf-links">
            {zurueck ? (
              <a className="kopf-zurueck" href={zurueck.adresse} aria-label={`Zurück: ${zurueck.name}`}>
                <Ikon name="links" />
                {zurueck.ikon && <Ikon name={zurueck.ikon} groesse="k" />}
                <span>{zurueck.name}</span>
              </a>
            ) : (
              <img className="app-zeichen" src="/symbol-192.png" alt="" />
            )}
          </div>
          <div className="app-kopf-titel">{titel}</div>
          <div className="app-kopf-rechts">{rechts}</div>
        </div>
        {aendern && gross ? (
          <a className="kopf-aendern" href={aendern}>
            <span className="kopf-aendern-wort">
              <h1>{gross}</h1>
              {unter && <span className="app-kopf-unter">{unter}</span>}
            </span>
            <span className="kopf-stift">
              <Ikon name="stift" groesse="k" /> ändern
            </span>
          </a>
        ) : (
          <>
            {gross && <h1>{gross}</h1>}
            {unter && <div className="app-kopf-unter">{unter}</div>}
          </>
        )}
        {children}
      </header>
    </>
  );
}

/** Der feste Fuß einer Seite – die Knöpfe bleiben unten, auch beim Scrollen. */
export function AppFuss({ children }: { children: ReactNode }) {
  return (
    <div className="app-fuss">
      <div className="app-fuss-innen">{children}</div>
    </div>
  );
}

/** „frage“: eine offene Frage an den Auftraggeber, sichtbar an ihrem Platz (wie im Mockup). */
export type ChipArt = "ok" | "warn" | "fehler" | "dunkel" | "lime" | "sp" | "frage";

export function Chip({ art, ikon, children }: { art?: ChipArt; ikon?: IkonName; children: ReactNode }) {
  return (
    <span className={`chip${art ? ` chip-${art}` : ""}`}>
      {ikon && <Ikon name={ikon} groesse="k" />}
      {children}
    </span>
  );
}

/**
 * Was später kommt, steht schon an seinem Platz – gestrichelt, mit dem
 * Wann (CLAUDE.md, Grundsatz 8). Die Form aus dem Mockup.
 */
export function Spaeter({ titel, wann, children }: { titel: string; wann: string; children?: ReactNode }) {
  return (
    <div className="spaeter-block">
      <div className="spaeter-kopf">
        <h4>{titel}</h4>
        <Chip art="sp" ikon="uhr">
          {wann}
        </Chip>
      </div>
      {children && <p>{children}</p>}
    </div>
  );
}

export type TippArt = "info" | "gelb" | "warn" | "grau" | "rot" | "frage";

/** Ein Hinweis im Inhalt: türkis (Info), gelb, orange (Achtung), grau (Erklärung), rot (Fehler), hell-orange (offene Frage). */
export function Tipp({ art = "info", ikon = "info", children }: { art?: TippArt; ikon?: IkonName; children: ReactNode }) {
  return (
    <div className={`tipp${art === "info" ? "" : ` tipp-${art}`}`} role={art === "rot" ? "alert" : undefined}>
      <Ikon name={ikon} />
      <div>{children}</div>
    </div>
  );
}

export interface AuswahlMoeglichkeit<T extends string> {
  wert: T;
  name: string;
  ikon?: IkonName;
  /** Vorbereitet, aber noch nicht gebaut: sichtbar, nicht wählbar. */
  gesperrt?: boolean;
}

/** Auswahlknöpfe (Segment): eine Möglichkeit an; gesperrte sind gestrichelt und zeigen, was noch kommt. */
export function Auswahl<T extends string>({
  name,
  moeglichkeiten,
  wert,
  aendern,
}: {
  name: string;
  moeglichkeiten: readonly AuswahlMoeglichkeit<T>[];
  wert: T;
  aendern: (wert: T) => void;
}) {
  return (
    <div className="auswahl" role="radiogroup" aria-label={name}>
      {moeglichkeiten.map((m) => (
        <button
          key={m.wert}
          type="button"
          role="radio"
          aria-checked={m.wert === wert}
          className={m.wert === wert ? "an" : undefined}
          disabled={m.gesperrt}
          title={m.gesperrt ? "Kommt später" : undefined}
          onClick={() => aendern(m.wert)}
        >
          {m.ikon && <Ikon name={m.ikon} groesse="k" />}
          {m.name}
        </button>
      ))}
    </div>
  );
}

/** Der Fortschrittsring „x/n“ in Lime. */
export function Ring({ wert, von }: { wert: number; von: number }) {
  const umfang = 2 * Math.PI * 27;
  const anteil = von > 0 ? Math.min(1, wert / von) : 0;
  return (
    <div className="ring" role="img" aria-label={`${wert} von ${von} fertig`}>
      <svg viewBox="0 0 64 64">
        <circle cx="32" cy="32" r="27" fill="none" stroke="#E9EDEB" strokeWidth="7" />
        {anteil > 0 && (
          <circle
            cx="32"
            cy="32"
            r="27"
            fill="none"
            stroke="#D4F000"
            strokeWidth="7"
            strokeLinecap="round"
            strokeDasharray={`${anteil * umfang} ${umfang}`}
          />
        )}
      </svg>
      <span className="ring-zahl">
        {wert}/{von}
      </span>
    </div>
  );
}

// --- Toast: eine kurze Bestätigung oben, verschwindet von selbst --------------------
// Seit E3c (S5; O94): `bleibt` – eine Meldung, die nicht untergehen darf
// („2 Fotos ließen sich nicht lesen“), bleibt stehen, bis man sie antippt.

let toastText: string | null = null;
let toastArt: "ok" | "fehler" = "ok";
let toastBleibt = false;
let toastNr = 0;
const toastZuhoerer = new Set<() => void>();

/**
 * Zeigt oben kurz eine Bestätigung, z. B. „Dach fertig“ – oder mit „fehler“
 * einen Hinweis, dass etwas nicht ging. Mit `bleibt` steht sie, bis man sie
 * antippt (oder die nächste kommt).
 */
export function toastZeigen(text: string, art: "ok" | "fehler" = "ok", { bleibt = false }: { bleibt?: boolean } = {}): void {
  toastText = text;
  toastArt = art;
  toastBleibt = bleibt;
  toastNr += 1;
  for (const z of toastZuhoerer) z();
}

/** Die Meldung weg – nach der Zeit oder, wenn sie bleibt, beim Antippen. */
function toastWeg(): void {
  toastText = null;
  for (const z of toastZuhoerer) z();
}

function toastAbonnieren(melden: () => void): () => void {
  toastZuhoerer.add(melden);
  return () => toastZuhoerer.delete(melden);
}

export function Toast() {
  const nr = useSyncExternalStore(
    toastAbonnieren,
    () => toastNr,
    () => 0,
  );
  useEffect(() => {
    if (nr === 0 || toastBleibt) return;
    const uhr = setTimeout(toastWeg, 2600);
    return () => clearTimeout(uhr);
  }, [nr]);
  const text = useSyncExternalStore(
    toastAbonnieren,
    () => toastText,
    () => null,
  );
  if (!text) return null;
  if (toastBleibt) {
    return (
      <div className={`toast toast-bleibt${toastArt === "fehler" ? " toast-fehler" : ""}`} role="alert" key={nr} onClick={toastWeg}>
        <Ikon name={toastArt === "fehler" ? "achtung" : "haken"} />
        <span className="toast-wort">{text}</span>
        <button type="button" className="toast-zu" onClick={toastWeg}>
          OK
        </button>
      </div>
    );
  }
  return (
    <div className={`toast${toastArt === "fehler" ? " toast-fehler" : ""}`} role="status" key={nr}>
      <Ikon name={toastArt === "fehler" ? "achtung" : "haken"} />
      {text}
    </div>
  );
}

// --- Netz: Meldet das Gerät Empfang? ---------------------------------------------------

function netzAbonnieren(melden: () => void): () => void {
  window.addEventListener("online", melden);
  window.addEventListener("offline", melden);
  return () => {
    window.removeEventListener("online", melden);
    window.removeEventListener("offline", melden);
  };
}

/** true, solange das Gerät nicht selbst „kein Netz“ meldet (Flugmodus, Keller). */
export function useNetz(): boolean {
  return useSyncExternalStore(
    netzAbonnieren,
    () => navigator.onLine !== false,
    () => true,
  );
}
